import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

export default function WhyNow() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="Why Now">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Four trends, converging now.
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-3xl">
          Demand reshaped. Build cost collapsed. The cost/reward equation just
          flipped.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
        <Force
          number="1"
          name="App Supply Explosion"
          lede="AI coding is flooding the stores."
        >
          Claude Code, Cursor, Codex, Lovable, v0 have collapsed the cost and
          time of shipping an app. Q1 2026 saw{" "}
          <span className="font-semibold text-slate-900">
            +84% YoY new App Store submissions
          </span>
          <SupLink to="sources">2</SupLink>. Every existing app now competes against a flood of
          fresh listings — visibility is scarcer than ever.
        </Force>
        <Force
          number="2"
          name="Discovery Shift"
          lede="Discovery splintered, ranking went semantic."
        >
          Apple's App Store search ranking is now LLM-fine-tuned
          <SupLink to="sources">3</SupLink>; Google followed with Ask Play, a Gemini discovery
          layer<SupLink to="sources">7</SupLink>. ChatGPT, Claude, Gemini, Perplexity shape where
          users look for apps<SupLink to="sources">4</SupLink>, and EU DMA opened iOS to alt
          storefronts in 2025<SupLink to="sources">5</SupLink>.{" "}
          <span className="font-semibold text-slate-900">
            Two-store, search-only, keyword-stuffed ASO is over.
          </span>
        </Force>
        <Force
          number="3"
          name="Localization Mandate"
          lede="Growth now requires going global."
        >
          Global IAP hit $167B in 2025<SupLink to="sources">6</SupLink>, with most growth outside
          any single market. AI translation just crossed the quality bar —{" "}
          <span className="font-semibold text-slate-900">
            high-quality localization across 180+ markets
          </span>{" "}
          is now affordable for mid-market publishers.
        </Force>
        <Force
          number="4"
          name="The Build Arbitrage"
          lede="Now the math works — for us, not for them."
          highlighted
        >
          What cost <span className="font-semibold text-slate-900">$22–25M
          and 26 FTE over 24 months</span> in 2022 now costs a fraction.
          AI-native development collapsed the build economics.{" "}
          <span className="font-semibold text-slate-900">
            A market that couldn't justify a venture-scale platform a few
            years ago absolutely can today
          </span>{" "}
          — and incumbents, weighted down by legacy codebases, will be
          inertially challenged to seize this arbitrage themselves.
        </Force>
      </div>

    </div>
  );
}

function Force({
  number,
  name,
  lede,
  children,
  highlighted,
}: {
  number: string;
  name: string;
  lede: string;
  children: ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div
      className={`flex flex-col rounded-xl p-5 md:p-6 ${
        highlighted
          ? "bg-blue-50 ring-2 ring-blue-400"
          : "bg-white shadow-sm ring-1 ring-slate-200"
      }`}
    >
      <div className="flex items-center gap-3 md:gap-4">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white md:h-9 md:w-9 md:text-base">
          {number}
        </span>
        <h3 className="text-xl font-bold leading-tight tracking-tight text-blue-600 md:text-3xl">
          {name}
        </h3>
      </div>
      <p className="mt-3 text-base font-semibold leading-snug text-slate-900 md:mt-4 md:text-xl">
        {lede}
      </p>
      <p className="mt-3 text-sm leading-relaxed text-slate-600 md:mt-3 md:text-base md:leading-snug">
        {children}
      </p>
    </div>
  );
}
